// Odberateľ (klient) — perzistentná Dexie tabuľka `counterparties` (Wave 1).
// dedupeCounterparties/filterCounterparties = čisté funkcie (derive z faktúr),
// ostávajú lebo ich používa backfill (seed z existujúcich faktúr) + UI.
// upsert/list/delete/backfill = perzistentné API nad db.counterparties.
import { listInvoices } from './records';
import { db, newCommonFields, putSetting } from './db';

// Dedup kľúč: ico (normalized, bez medzier) ak je neprázdne → "12345678".
// Inak name.toLowerCase().trim(). Tým zlučíme rovnakú firmu aj keď používateľ
// raz zadal IČO a raz nie.
function cpKey(inv) {
  const ico = (inv.counterparty?.ico || inv.counterparty_ico || '').replace(/\s/g, '').trim();
  if (ico) return `ico:${ico}`;
  const name = (inv.counterparty?.name || inv.counterparty_name || '').toLowerCase().trim();
  return `name:${name}`;
}

// dedupeCounterparties(invoices) — čistá funkcia (testovateľná bez DB).
// Vstup: pole faktúr (module='invoice'). Každá nesie counterparty {name,ico,dic,ic_dph,address}
// + denormalizované counterparty_name/ico/dic.
// Výstup: pole {name, ico, dic, ic_dph, address} zoradené zostupne podľa počtu výskytov
// (najčastejší hore). Pri zhode počtu abecedne podľa name.
export function dedupeCounterparties(invoices) {
  const counts = new Map(); // key → count
  const first = new Map();  // key → {name, ico, dic, ic_dph, address} z prvého výskytu
  const nameToKey = new Map(); // name(lower) → kanonický key (zjednotí name-keyed a ico-keyed pre tú istú firmu)

  for (const inv of invoices) {
    const name = (inv.counterparty?.name || inv.counterparty_name || '').trim();
    if (!name) continue; // preskočíme faktúry bez odberateľa

    const nameLower = name.toLowerCase();
    let key = cpKey(inv);
    // Ak sme túto firmu už videli pod menom (napr. prvý výskyt bez IČO → name:firma,
    // teraz príde s IČO → ico:...), použijeme existujúci kľúč aby sa zlúčili a IČO sa
    // doplnilo enrichmentom namiesto vytvorenia 2. záznamu.
    if (nameToKey.has(nameLower)) {
      key = nameToKey.get(nameLower);
    } else {
      nameToKey.set(nameLower, key);
    }
    counts.set(key, (counts.get(key) || 0) + 1);

    if (!first.has(key)) {
      // Preferujeme bohatší záznam (s IČO/DIČ) pri prvom výskyte.
      const cp = inv.counterparty || {};
      first.set(key, {
        name: cp.name || inv.counterparty_name || name,
        ico: (cp.ico || inv.counterparty_ico || '').trim(),
        dic: (cp.dic || inv.counterparty_dic || '').trim(),
        ic_dph: (cp.ic_dph || inv.counterparty_icdph || '').trim(),
        address: (cp.address || '').trim(),
      });
    } else {
      // Ak neskorší výskyt má viac informácií (napr. pridal IČO), enrichni.
      const cp = inv.counterparty || {};
      const existing = first.get(key);
      if (!existing.ico && (cp.ico || inv.counterparty_ico)) {
        existing.ico = (cp.ico || inv.counterparty_ico || '').trim();
      }
      if (!existing.dic && (cp.dic || inv.counterparty_dic)) {
        existing.dic = (cp.dic || inv.counterparty_dic || '').trim();
      }
      if (!existing.ic_dph && (cp.ic_dph || inv.counterparty_icdph)) {
        existing.ic_dph = (cp.ic_dph || inv.counterparty_icdph || '').trim();
      }
      if (!existing.address && cp.address) {
        existing.address = cp.address.trim();
      }
    }
  }

  // Zoraď zostupne podľa počtu výskytov, pri zhode abecedne.
  return [...first.entries()]
    .sort(([ka, a], [kb, b]) => {
      const diff = (counts.get(kb) || 0) - (counts.get(ka) || 0);
      if (diff !== 0) return diff;
      return a.name.localeCompare(b.name, 'sk');
    })
    .map(([, cp]) => cp);
}

// filterCounterparties(list, query) — čistá funkcia (testovateľná bez DB).
// Prázdny / whitespace query → vráti prvých N (default 6).
// Inak case-insensitive filter: match podľa name (contains) ALEBO ico (contains/prefix).
const DEFAULT_LIMIT = 6;

export function filterCounterparties(list, query, limit = DEFAULT_LIMIT) {
  const q = (query || '').trim();
  if (!q) return list.slice(0, limit);

  const ql = q.toLowerCase();
  return list
    .filter((cp) => {
      const nameMatch = (cp.name || '').toLowerCase().includes(ql);
      const icoMatch = cp.ico && cp.ico.includes(ql);
      return nameMatch || icoMatch;
    })
    .slice(0, limit);
}

// ─── Perzistentná tabuľka klientov (db.counterparties) ───────────────────────

// Normalizácia kľúčov zhody.
function normIco(ico) {
  return String(ico || '').replace(/\s/g, '').trim();
}
function normName(name) {
  return String(name || '').trim().toLowerCase();
}

// Kľúč zhody pre upsert: neprázdne IČO ak je, inak normalizované meno.
// { kind:'ico'|'name', value } alebo null keď ani meno nie je.
function matchKey({ ico, name }) {
  const ci = normIco(ico);
  if (ci) return { kind: 'ico', value: ci };
  const nn = normName(name);
  if (nn) return { kind: 'name', value: nn };
  return null;
}

// Nájdi existujúci aktívny záznam podľa IČO (presná zhoda) alebo norm. mena.
// Keď je IČO zadané: najprv presná zhoda IČO. Ak nenájde, skús meno — ale LEN
// záznamy s prázdnym IČO (tie sa môžu enrichnúť doplnením IČO). Záznam s iným
// neprázdnym IČO je iná firma a NESMIE sa zlúčiť (dvaja klienti rovnaké meno,
// rôzne IČO). Keď IČO nie je, name-zhoda platí na hocijaký záznam.
async function findCounterparty({ ico, name }) {
  const ci = normIco(ico);
  const nn = normName(name);
  if (ci) {
    const byIco = await db.counterparties.where('ico').equals(ci).first();
    if (byIco && byIco.deleted_at == null) return byIco;
    if (nn) {
      const byName = await db.counterparties
        .where('name_normalized')
        .equals(nn)
        .filter((r) => r.deleted_at == null && !normIco(r.ico))
        .first();
      if (byName) return byName;
    }
    return null;
  }
  if (nn) {
    const byName = await db.counterparties
      .where('name_normalized')
      .equals(nn)
      .filter((r) => r.deleted_at == null)
      .first();
    if (byName) return byName;
  }
  return null;
}

// upsertCounterparty(cp) — vytvor alebo enrichni klienta.
// cp = { name (povinné), ico?, dic?, ic_dph?, address? }
// Zhoda = norm. IČO (ak neprázdne), inak name.toLowerCase().trim().
// Pri zhode: doplní LEN chýbajúce polia (nehasí existujúce), use_count++, last_used_at=teraz.
// Inak vytvorí nový (use_count=1). Vracia uložený záznam, alebo null keď bez mena.
export async function upsertCounterparty(cp) {
  const name = String(cp?.name || '').trim();
  if (!name) return null;
  const key = matchKey(cp);
  if (!key) return null;

  const now = new Date().toISOString();
  const existing = await findCounterparty(cp);

  if (existing) {
    const ci = normIco(cp.ico);
    const patch = {
      // enrich chýbajúce — nehas existujúce
      name: existing.name || name,
      ico: existing.ico || ci,
      dic: existing.dic || String(cp.dic || '').trim(),
      ic_dph: existing.ic_dph || String(cp.ic_dph || '').trim(),
      address: existing.address || String(cp.address || '').trim(),
      email: existing.email || String(cp.email || '').trim(),
      phone: existing.phone || String(cp.phone || '').trim(),
      name_normalized: existing.name_normalized || normName(name),
      use_count: (existing.use_count || 0) + 1,
      last_used_at: now,
      updated_at: now,
      rev: (existing.rev || 1) + 1,
    };
    await db.counterparties.update(existing.id, patch);
    return { ...existing, ...patch };
  }

  const record = {
    ...newCommonFields(),
    name,
    name_normalized: normName(name),
    ico: normIco(cp.ico),
    dic: String(cp.dic || '').trim(),
    ic_dph: String(cp.ic_dph || '').trim(),
    address: String(cp.address || '').trim(),
    email: String(cp.email || '').trim(),
    phone: String(cp.phone || '').trim(),
    use_count: 1,
    last_used_at: now,
  };
  await db.counterparties.add(record);
  return record;
}

// listCounterparties({ query } = {}) — aktívni klienti pre autocomplete.
// Zoradenie: prefix-match na query (name/ico) hore, potom use_count desc,
// potom last_used_at desc. Prázdny query → len use_count desc, last_used_at desc.
// getCounterparty(id) — jeden klient podľa id (aj soft-deleted; UI si rieši).
export async function getCounterparty(id) {
  return (await db.counterparties.get(id)) || null;
}

export async function listCounterparties({ query = '' } = {}) {
  const all = await db.counterparties
    .filter((r) => r.deleted_at == null)
    .toArray();

  const q = String(query || '').trim().toLowerCase();

  const prefixRank = (cp) => {
    if (!q) return 0;
    const nameHit = (cp.name_normalized || '').startsWith(q);
    const icoHit = (cp.ico || '').startsWith(q);
    return nameHit || icoHit ? 1 : 0;
  };

  // Pri neprázdnom query odfiltruj tých čo nematchujú vôbec (name/ico contains).
  const filtered = q
    ? all.filter((cp) => {
        return (
          (cp.name_normalized || '').includes(q) ||
          (cp.ico || '').includes(q)
        );
      })
    : all;

  filtered.sort((a, b) => {
    const pr = prefixRank(b) - prefixRank(a);
    if (pr !== 0) return pr;
    const uc = (b.use_count || 0) - (a.use_count || 0);
    if (uc !== 0) return uc;
    const al = a.last_used_at || '';
    const bl = b.last_used_at || '';
    if (al !== bl) return al < bl ? 1 : -1;
    return (a.name || '').localeCompare(b.name || '', 'sk');
  });

  return filtered;
}

// deleteCounterparty(id) — soft-delete (deleted_at), záznam ostáva v DB.
export async function deleteCounterparty(id) {
  const now = new Date().toISOString();
  const rec = await db.counterparties.get(id);
  if (!rec) return false;
  await db.counterparties.update(id, { deleted_at: now, updated_at: now, rev: (rec.rev || 1) + 1 });
  return true;
}

// updateCounterparty(id, fields) — explicitná úprava klienta z UI.
// Na rozdiel od upsertCounterparty (enrich-only, nehasí existujúce) toto PREPISUJE
// zadané polia. Editovateľné: name, ico, dic, ic_dph, address. name nesmie byť
// prázdne. Prepočíta name_normalized pri zmene mena, bumpne updated_at + rev (LWW
// sync to zoberie). Nedotýka sa use_count, last_used_at, deleted_at, id.
// Vracia aktualizovaný záznam, alebo false keď id neexistuje.
export async function updateCounterparty(id, fields = {}) {
  const rec = await db.counterparties.get(id);
  if (!rec) return false;

  const patch = { updated_at: new Date().toISOString(), rev: (rec.rev || 1) + 1 };

  if (fields.name !== undefined) {
    const nm = String(fields.name || '').trim();
    if (!nm) throw new Error('name required');
    patch.name = nm;
    patch.name_normalized = normName(nm);
  }
  if (fields.ico !== undefined) patch.ico = normIco(fields.ico);
  if (fields.dic !== undefined) patch.dic = String(fields.dic || '').trim();
  if (fields.ic_dph !== undefined) patch.ic_dph = String(fields.ic_dph || '').trim();
  if (fields.address !== undefined) patch.address = String(fields.address || '').trim();
  if (fields.email !== undefined) patch.email = String(fields.email || '').trim();
  if (fields.phone !== undefined) patch.phone = String(fields.phone || '').trim();
  // Pamäť vzťahu (Phase 0.5): „tento klient platí vždy neskôr — neupozorňovať".
  // Boolean flag; ranný briefing naň netlačí (netvorí buzz z jeho po-splatnosti).
  if (fields.reminder_muted !== undefined) patch.reminder_muted = !!fields.reminder_muted;

  await db.counterparties.update(id, patch);
  return { ...rec, ...patch };
}

// backfillCounterpartiesFromInvoices() — jednorazový seed z existujúcich faktúr.
// Idempotentný: každý odberateľ ide cez upsertCounterparty (zhoda IČO/meno),
// takže opakované volanie nevytvára duplikáty (len zvyšuje use_count). Vracia
// počet spracovaných odberateľov.
export async function backfillCounterpartiesFromInvoices() {
  const invoices = await listInvoices();
  const derived = dedupeCounterparties(invoices);
  for (const cp of derived) {
    await upsertCounterparty(cp);
  }
  return derived.length;
}

// Guard flag — backfill prebehne LAZY iba raz (kým je tabuľka „panenská").
const CP_BACKFILLED_KEY = 'v2_cp_backfilled';

async function ensureBackfilled() {
  try {
    const flag = await db.settings.get(CP_BACKFILLED_KEY);
    if (flag?.value) return;
    await backfillCounterpartiesFromInvoices();
    await putSetting({ key: CP_BACKFILLED_KEY, value: true });
  } catch (e) {
    console.warn('[v2] counterparties backfill failed', e);
  }
}

// listKnownCounterparties() — async wrapper pre UI autocomplete.
// Lazy backfill (raz) → číta z perzistentnej tabuľky. Tvar { name, ico, dic,
// ic_dph, address } ostáva rovnaký ako predtým (UI sa nemení).
export async function listKnownCounterparties(query = '') {
  await ensureBackfilled();
  const list = await listCounterparties({ query });
  return list.map((cp) => ({
    id: cp.id,
    name: cp.name,
    ico: cp.ico || '',
    dic: cp.dic || '',
    ic_dph: cp.ic_dph || '',
    address: cp.address || '',
  }));
}
